import { useEffect, useRef, useState } from "react";
import type { DashboardData, ExpenseInput, Owner, Transaction } from "../../types";
import type { PriceObservation } from "../../types/receipt";
import type { ConfirmState } from "../../components/ConfirmDialog";
import { saveEdit, type EditJournal } from "./edit-save";
import { reconciledMonths, reconciliationMonthLabel } from "../../lib/reconciliation";
import { transactionMatches } from "../../lib/transactions";
import { api } from "../../lib/api";
import { transactionDraft } from "./transaction-draft";

// The transaction open in the detail sheet: which one it is (kept in the URL, so Back closes it),
// and editing or deleting it. An edit is journalled on the device as it goes, because a transaction
// and its tracked prices are saved one after another and a lost tab must not lose the rest.

export function useTransactionDetail({ data, profile, priceRows, refresh, setConfirm }: {
  data: DashboardData | null;
  profile: Owner | null;
  priceRows: PriceObservation[];
  refresh: (force?: boolean) => Promise<unknown>;
  setConfirm: (confirm: ConfirmState | null) => void;
}) {
  const [detail, setDetailState] = useState<Transaction | null>(null),
    [editingDetail, setEditingDetail] = useState(false),
    [detailBusy, setDetailBusy] = useState(false),
    [detailError, setDetailError] = useState(""),
    [detailPrices, setDetailPrices] = useState<PriceObservation[]>([]);
  function setDetail(transaction: Transaction | null) {
    const url = new URL(window.location.href);
    if (transaction) {
      url.searchParams.set("transaction", transaction.id);
      window.history.pushState(
        { ...window.history.state, detailNavigation: true },
        "",
        url,
      );
    } else {
      if (window.history.state?.detailNavigation) {
        window.history.back();
        setDetailState(null);
        setEditingDetail(false);
        return;
      }
      url.searchParams.delete("transaction");
      window.history.replaceState(
        { ...window.history.state, detailNavigation: false },
        "",
        url,
      );
    }
    setDetailState(transaction);
    setEditingDetail(false);
    setDetailError("");
  }
  useEffect(() => {
    const sync = () => {
      const id = new URLSearchParams(window.location.search).get("transaction");
      setDetailState(data?.transactions.find((t) => t.id === id) || null);
      setEditingDetail(false);
    };
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, [data]);
  async function updateDetail(expense: ExpenseInput, reconciliationApproved = false) {
    if (!detail || detailSaveLock.current) return;
    const affected = data && !transactionMatches(detail, expense) ? reconciledMonths(data, [detail.date, expense.date]) : [];
    if (!reconciliationApproved && affected.length) {
      setConfirm({ title: "Reopen a reconciled month?", message: `This change affects ${affected.map(reconciliationMonthLabel).join(", ")}. Saving it will reopen the affected balance reviews.`, confirmLabel: "Save anyway", onConfirm: () => updateDetail(expense, true) });
      return;
    }
    detailSaveLock.current = true;
    setDetailBusy(true);
    setDetailError("");
    try {
      const journal = editJournal.current?.started ? editJournal.current : { ...editJournal.current, transaction: detail, originalPrices: editPriceSnapshot.current, expense, started: false, transactionSaved: false, completed: [] };
      await saveEdit(journal, api, persistEdit);
      // No separate price fetch: the refresh below changes `fetchedAt`, which reloads the prices.
      setEditingDetail(false);
      localStorage.removeItem(editKey(detail.id));
      editJournal.current = null;
      await refresh(true);
    } catch (e) {
      if (editJournal.current?.transactionSaved) {
        const message = (e as Error).message;
        setDetailError(
          `Transaction saved. Your remaining item changes are kept here. Retry remaining changes. ${message}`,
        );
      } else setDetailError((e as Error).message);
    } finally {
      setDetailBusy(false);
      detailSaveLock.current = false;
    }
  }
  const detailSaveLock = useRef(false);
  const editPriceSnapshot = useRef<PriceObservation[]>([]);
  const editJournal = useRef<EditJournal | null>(null);
  const editKey = (id: string) => `barnyard-edit-${profile}-${id}`;
  function persistEdit(value: EditJournal) {
    localStorage.setItem(editKey(value.transaction.id), JSON.stringify(value));
    editJournal.current = structuredClone(value);
  }
  useEffect(() => {
    if (!detail) {
      setDetailPrices([]);
      return;
    }
    setDetailPrices(priceRows.filter((p) => p.transactionId === detail.id));
  // Follows the open transaction's id, not edits made to its fields.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, priceRows]);

  async function deleteDetailConfirmed(t: Transaction) {
    setDetailBusy(true);
    setDetailError("");
    try {
      await api("transactions/delete", { id: t.id, expected: t });
      setDetail(null);
      await refresh(true);
    } catch (e) {
      setDetailError((e as Error).message);
    } finally {
      setDetailBusy(false);
    }
  }
  function askDeleteDetail() {
    if (!detail) return;
    const target = detail;
    setConfirm({
      title: "Delete transaction?",
      message: `Delete ${target.title || target.id}? Linked prices will be kept as standalone observations with a note about their original transaction.${data && reconciledMonths(data, [target.date]).length ? " This will reopen affected balance reviews." : ""}`,
      confirmLabel: "Delete",
      danger: true,
      onConfirm: () => deleteDetailConfirmed(target),
    });
  }
  return {
    detail, setDetail, editingDetail, setEditingDetail, detailBusy, setDetailBusy,
    detailError, setDetailError, detailPrices, setDetailPrices,
    editKey, editJournal, editPriceSnapshot, persistEdit,
    transactionDraft: (t: Transaction) => transactionDraft(t, detailPrices),
    updateDetail, askDeleteDetail,
  };
}
